/**
 * Fair Style Space — AIRA chat panel (Unit 6): the thread → the panel's bubbles.
 * Visual: Figma `42:6508` / Message `42:7627`.
 * The conversation lives in `features/aira` (one thread for the panel and `/aira`);
 * this file only shapes it for the panel and turns the stylist's actions into canvas state.
 */

import {
  airaAskOf,
  airaButtonsOf,
  airaCapsuleReadyOf,
  airaProposeOutfitsOf,
  airaWarnOf,
  buildAiraEnvelope,
  sameAiraCanvasItem,
  type AiraAudience,
  type AiraItemSource,
  type AiraCanvasItemRef,
  type AiraChatMessage,
  type AiraContextEnvelope,
  type AiraProposedOutfit,
  type AiraSurface,
  type AiraVocabEntry,
  type AiraCommand,
} from '@/features/aira/api'
import {
  FAIR_STYLE_SPACE_CANVAS_SLOT_DEFS,
  canvasSlotForOutfitItem,
  createEmptyCanvasSlots,
  findWardrobeItemById,
  outfitItemsToCanvas,
  type FairStyleSpaceCanvasSlotId,
  type FairStyleSpaceCanvasSlots,
} from './fair-style-space-canvas.data'
import type { FairStyleSpaceLookOption } from './fair-style-space-look-options.data'
import type { FairStyleSpaceWardrobeItem } from './fair-style-space-wardrobe.data'

export type FairStyleSpaceAiraChipAction =
  | { kind: 'occasion'; code: string; label: string }
  | { kind: 'custom' }
  | { kind: 'say'; text: string }
  | { kind: 'switch-look'; index: number }
  /** A chat button from the stylist (`buttons` action) — the canvas runs the command. */
  | { kind: 'command'; command: AiraCommand }
  /** An `ask` reply that also fills the brief: `answers[answerKey] = value` (§7b). */
  | { kind: 'answer'; text: string; answerKey?: string | null; value: string }
  /** A `multi` ask's Done: the joined labels as the line, the values into the brief. */
  | { kind: 'answer-multi'; text: string; answerKey?: string | null; values: string[] }

export interface FairStyleSpaceAiraChip {
  id: string
  /** i18n key under fair_style_space.playground.*; `label` wins when set. */
  labelKey: string
  label?: string
  icon?: 'ai'
  action: FairStyleSpaceAiraChipAction
  /** `multi` ask: what Done collects for this chip. */
  value?: string
}

/** Figma "Capsule preview in chat": four pieces, the name, → the capsule page. */
export interface FairStyleSpaceAiraCapsuleCard {
  capsuleId: number
  name: string
  thumbs: FairStyleSpaceAiraThumb[]
}

export interface FairStyleSpaceAiraThumb {
  id: string
  imageSrc: string
  name: string
}

export interface FairStyleSpaceAiraLookCard {
  index: number
  /** Empty for a stylist's draft — the panel labels it "Option n". */
  tag: string
  thumbs: FairStyleSpaceAiraThumb[]
}

export interface FairStyleSpaceAiraProductCard {
  name: string
  imageSrc: string
  captionKey: string
}

export interface FairStyleSpaceAiraMessage {
  id: string
  role: 'aira' | 'user'
  /** i18n key under fair_style_space.playground.* OR raw text when `rawText` is set */
  textKey?: string
  textParams?: Record<string, string | number>
  rawText?: string
  captionKey?: string
  chips?: FairStyleSpaceAiraChip[]
  thumbs?: FairStyleSpaceAiraThumb[]
  lookCards?: FairStyleSpaceAiraLookCard[]
  product?: FairStyleSpaceAiraProductCard
  /** The stylist's warning about a piece on the canvas (`warn` action). */
  warn?: string
  /** `ask` with `mode: multi`: the chips check and Done sends them together. */
  askMode?: 'single' | 'multi'
  askAnswerKey?: string | null
  /** `capsule_ready`: the built capsule's card. */
  capsule?: FairStyleSpaceAiraCapsuleCard
  status?: 'sending' | 'degraded' | 'failed'
  /** Set when the line can be asked again with the same id. */
  retryClientMessageId?: string
}

export function createFairStyleSpaceAiraGreetMessage(
  occasions: readonly { code: string; label: string }[] = [],
): FairStyleSpaceAiraMessage {
  return {
    id: 'greet',
    role: 'aira',
    textKey: 'chat_greet',
    captionKey: 'chat_greet_caption',
    chips: [
      ...occasions.map((occasion) => ({
        id: `occasion-${occasion.code}`,
        labelKey: '',
        label: occasion.label,
        action: { kind: 'occasion' as const, code: occasion.code, label: occasion.label },
      })),
      {
        id: 'custom',
        labelKey: 'chat_describe',
        icon: 'ai',
        action: { kind: 'custom' },
      },
    ],
  }
}

export function thumbsFromWardrobeItems(
  items: readonly FairStyleSpaceWardrobeItem[],
  limit = 4,
): FairStyleSpaceAiraThumb[] {
  return items.slice(0, limit).map((item) => ({
    id: item.id,
    imageSrc: item.imageSrc,
    name: item.name,
  }))
}

/** A reply without backend text explains itself by the reason the client recorded. */
function degradedTextKey(message: AiraChatMessage): { textKey: string; textParams?: Record<string, string | number> } {
  switch (message.localReason) {
    case 'rate_limited':
      return { textKey: 'chat_rate_limited', textParams: { seconds: message.retryAfter ?? 60 } }
    case 'rejected':
      return { textKey: 'chat_rejected' }
    case 'network':
    case 'timeout':
      return { textKey: 'chat_offline' }
    default:
      return { textKey: 'chat_degraded' }
  }
}

/** The thread as the panel draws it; every line of both surfaces, oldest first. */
export function mapAiraThreadToPanelMessages(
  messages: readonly AiraChatMessage[],
  options: {
    /** `playground_chat_commands` — off: the stylist's buttons are not offered (their commands would not run). */
    commands?: boolean
  } = {},
): FairStyleSpaceAiraMessage[] {
  return messages.map((message) => {
    if (message.role === 'user') {
      return {
        id: message.key,
        role: 'user',
        rawText: message.text,
        status: message.delivery === 'sending' ? 'sending' : undefined,
      }
    }
    const view: FairStyleSpaceAiraMessage = { id: message.key, role: 'aira' }
    if (message.text) view.rawText = message.text
    else if (message.delivery === 'degraded' || message.delivery === 'failed') Object.assign(view, degradedTextKey(message))
    if (message.delivery === 'degraded' || message.delivery === 'failed') {
      view.status = message.delivery
      if (message.retryable && message.clientMessageId) view.retryClientMessageId = message.clientMessageId
      // the backend may name the reason (`wardrobe_too_small`): the way out is a pair of chips
      const reason = (message.actions.find((a) => a.type === 'answer') as { reason?: string } | undefined)?.reason
      if (reason === 'wardrobe_too_small') {
        view.textKey = 'chat_too_few_items'
        view.rawText = undefined
        view.chips = [
          { id: `${message.key}-mix`, labelKey: 'chip_use_mix', action: { kind: 'command', command: { type: 'set_source', source: 'mixed' } } },
          { id: `${message.key}-catalog`, labelKey: 'chip_use_catalog', action: { kind: 'command', command: { type: 'set_source', source: 'catalog' } } },
        ]
        view.retryClientMessageId = undefined
      }
    }
    const proposal = airaProposeOutfitsOf(message)
    if (proposal?.outfits.length) {
      view.captionKey = 'chat_drafts_caption'
      view.lookCards = proposal.outfits.map((outfit, index) => ({
        index,
        tag: '',
        thumbs: outfit.items.slice(0, 3).map((item, itemIndex) => ({
          id: `${message.key}-${index}-${itemIndex}`,
          imageSrc: item.image,
          name: '',
        })),
      }))
    }
    const warn = airaWarnOf(message)
    if (warn) view.warn = warn.detail
    // `ask`: reply chips — tapping says the chip's text (and fills the brief when the chip
    // carries a value); `buttons`: commands for the canvas
    const ask = airaAskOf(message)
    const buttons = options.commands ? airaButtonsOf(message) : null
    if (ask) {
      view.askMode = ask.mode === 'multi' ? 'multi' : 'single'
      view.askAnswerKey = ask.answerKey ?? null
    }
    const chips: FairStyleSpaceAiraChip[] = [
      ...(ask?.chips ?? []).map((chip, index) => ({
        id: `${message.key}-ask-${index}`,
        labelKey: '',
        label: chip.label,
        value: chip.value ?? chip.label,
        action:
          chip.value != null || ask?.answerKey
            ? {
                kind: 'answer' as const,
                text: chip.text,
                answerKey: ask?.answerKey ?? null,
                value: chip.value ?? chip.label,
              }
            : { kind: 'say' as const, text: chip.text },
      })),
      ...(buttons?.buttons ?? []).map((button, index) => ({
        id: `${message.key}-btn-${index}`,
        labelKey: '',
        label: button.label,
        action: { kind: 'command' as const, command: button.command },
      })),
    ]
    if (chips.length) view.chips = chips
    const capsule = airaCapsuleReadyOf(message)
    if (capsule) {
      view.capsule = {
        capsuleId: capsule.capsuleId,
        name: capsule.name,
        thumbs: capsule.items.slice(0, 4).map((item, index) => ({
          id: `${message.key}-capsule-${index}`,
          imageSrc: item.image,
          name: '',
        })),
      }
    }
    return view
  })
}

/* ——— actions → canvas ——— */

export interface FairStyleSpaceDraftLooks {
  options: FairStyleSpaceLookOption[]
  /** Every piece the drafts carry (catalogue ones included) so the canvas can draw them. */
  items: FairStyleSpaceWardrobeItem[]
}

/** `propose_outfits` → drafts on the strip; the pieces are the outfit-item shape the canvas knows. */
export function proposedOutfitsToLookOptions(
  outfits: readonly AiraProposedOutfit[],
  messageKey: string,
): FairStyleSpaceDraftLooks {
  const options: FairStyleSpaceLookOption[] = []
  const items: FairStyleSpaceWardrobeItem[] = []
  const known = new Set<string>()
  outfits.forEach((outfit, index) => {
    const canvas = outfitItemsToCanvas(
      outfit.items.map((item) => ({
        productId: item.productId,
        wardrobeProductOverviewId: item.wardrobeProductOverviewId,
        image: item.image,
        typeId: item.typeId,
        subcategoryId: item.subcategoryId ?? 0,
        mainCategoryId: item.mainCategoryId ?? 0,
        variantId: item.variantId ?? null,
        name: item.name ?? null,
        brand: item.brand ?? null,
      })),
    )
    if (Object.values(canvas.slots).every((value) => value === null)) return
    options.push({
      id: `${messageKey}-draft-${index}`,
      tag: '',
      edited: false,
      slots: canvas.slots,
      draftOf: messageKey,
    })
    for (const item of canvas.items) {
      if (known.has(item.id)) continue
      known.add(item.id)
      items.push(item)
    }
  })
  return { options, items }
}

/** The slot holding the piece a `warn` action points at, if it is on the canvas. */
export function findCanvasSlotForPiece(
  slots: FairStyleSpaceCanvasSlots,
  items: readonly FairStyleSpaceWardrobeItem[],
  piece: AiraCanvasItemRef,
): FairStyleSpaceCanvasSlotId | null {
  for (const def of FAIR_STYLE_SPACE_CANVAS_SLOT_DEFS) {
    const item = findWardrobeItemById(items, slots[def.id])
    if (!item) continue
    const ref: AiraCanvasItemRef =
      item.overviewId != null ? { wardrobeProductOverviewId: item.overviewId } : { productId: item.productId ?? null }
    if (sameAiraCanvasItem(ref, piece)) return def.id
  }
  return null
}

/**
 * The slot a `modify_outfit` names by ML type: the filled one of that type first,
 * else where a piece of that type would land on an empty canvas.
 */
export function findCanvasSlotForType(
  slots: FairStyleSpaceCanvasSlots,
  items: readonly FairStyleSpaceWardrobeItem[],
  typeId: number | null,
): FairStyleSpaceCanvasSlotId | null {
  if (typeId == null) return null
  for (const def of FAIR_STYLE_SPACE_CANVAS_SLOT_DEFS) {
    const item = findWardrobeItemById(items, slots[def.id])
    if (item?.typeId === typeId) return def.id
  }
  return canvasSlotForOutfitItem({ typeId, mainCategoryId: 0 }, createEmptyCanvasSlots())
}

/* ——— envelope ——— */

/**
 * What the stylist gets to see about the canvas. Only ids travel: the backend
 * checks they are the caller's and describes the pieces itself, so the model
 * never sees a name the user did not upload.
 */
export function buildFairStyleSpaceAiraContext(input: {
  surface?: AiraSurface
  canvasItems: readonly FairStyleSpaceWardrobeItem[]
  occasion?: string | null
  season?: string | null
  outfitId?: number | null
  capsuleId?: number | null
  audience?: string | null
  /** Pinned pieces — the stylist deals around them (contract §1). */
  keep?: readonly FairStyleSpaceWardrobeItem[]
  /** The selected piece — the chat's context pill (contract §1). */
  focusItem?: FairStyleSpaceWardrobeItem | null
  itemSource?: AiraItemSource | null
  answers?: Record<string, string | string[]> | null
}): AiraContextEnvelope {
  const ref = (item: FairStyleSpaceWardrobeItem) => ({ productId: item.productId, overviewId: item.overviewId })
  return buildAiraEnvelope({
    surface: input.surface ?? (input.capsuleId != null ? 'capsule' : 'playground'),
    canvasItems: input.canvasItems.map(ref),
    occasion: input.occasion,
    season: input.season,
    outfitId: input.outfitId,
    capsuleId: input.capsuleId,
    audience: toAiraAudience(input.audience),
    keep: input.keep?.map(ref),
    focusItem: input.focusItem ? ref(input.focusItem) : null,
    itemSource: input.itemSource,
    answers: input.answers,
  })
}

/** The Style Space route says `women | men | kids`; the old persona words are mapped, anything else dropped. */
export function toAiraAudience(value: string | null | undefined): AiraAudience | null {
  switch (value) {
    case 'women':
    case 'woman':
      return 'women'
    case 'men':
    case 'man':
      return 'men'
    case 'kids':
      return 'kids'
    default:
      return null
  }
}

/** A chip label: the translation for the code when the app has one, else the backend's label. */
export function vocabChipLabel(
  entry: AiraVocabEntry,
  group: 'occasions' | 'seasons' | 'styles',
  te: (key: string) => boolean,
  t: (key: string) => string,
): string {
  const key = `fair_style_space.playground.${group}.${entry.code}`
  return te(key) ? t(key) : entry.label
}
